<template>
  <div class="home-page">
    

    <!-- 数据概览卡片 -->
    <div class="stats-section">
      <div class="stat-card" v-for="(item, index) in statsList" :key="index" @click="goToDetail(item.route)">
        <div class="stat-icon" :style="{ backgroundColor: item.bgColor }">
          <i :class="item.icon"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ item.value }}</div>
          <div class="stat-label">{{ item.label }}</div>
        </div>
        <div class="stat-trend" :class="item.trend >= 0 ? 'up' : 'down'">
          <i :class="item.trend >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
          <span>{{ Math.abs(item.trend) }}%</span>
        </div>
      </div>
    </div>

    <!-- 中间区域：快捷入口 + 待办事项 -->
    <div class="middle-section">
      <!-- 快捷入口 -->
      <div class="quick-access">
        <div class="section-title">
          <span>快捷入口</span>
          <el-link type="primary" @click="goToAllModules">全部模块</el-link>
        </div>
        <div class="quick-grid">
          <div class="quick-item" v-for="(item, index) in quickAccessList" :key="index" @click="goToRoute(item.route)">
            <div class="quick-icon" :style="{ backgroundColor: item.color + '20', color: item.color }">
              <i :class="item.icon"></i>
            </div>
            <span class="quick-name">{{ item.name }}</span>
          </div>
        </div>
      </div>

      <!-- 待办事项 -->
      <div class="todo-section">
        <div class="section-title">
          <span>待办事项</span>
          <el-badge :value="todoList.length" v-if="todoList.length > 0" />
        </div>
        <div class="todo-list">
          <div class="todo-item" v-for="(item, index) in todoList" :key="index" @click="handleTodo(item)">
            <div class="todo-dot" :class="item.priority"></div>
            <div class="todo-content">
              <div class="todo-title">{{ item.title }}</div>
              <div class="todo-time">{{ item.time }}</div>
            </div>
            <i class="el-icon-arrow-right todo-arrow"></i>
          </div>
          <div class="empty-todo" v-if="todoList.length === 0">
            <i class="el-icon-check"></i>
            <span>暂无待办事项</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部区域：各界面访问量 + 访问统计 -->
    <div class="bottom-section">
      <!-- 各界面访问量 -->
      <div class="notice-section">
        <div class="section-title">
          <span>各界面访问量</span>
          <el-link type="primary" @click="goToVisitLogs">查看全部</el-link>
        </div>
        <div class="page-visit-list">
          <div class="page-visit-item" v-for="(item, index) in pageVisitList" :key="index">
            <div class="page-rank" :class="{'top3': index < 3}">{{ index + 1 }}</div>
            <div class="page-info">
              <div class="page-title">{{ item.page_title || item.page_url }}</div>
              <div class="page-url">{{ item.page_url }}</div>
            </div>
            <div class="page-count">
              <span class="count-num">{{ item.count }}</span>
              <span class="count-label">次访问</span>
            </div>
          </div>
          <div class="empty-page-visit" v-if="pageVisitList.length === 0">
            <i class="el-icon-data-line"></i>
            <span>暂无访问数据</span>
          </div>
        </div>
      </div>

      <!-- 省市访问量统计 -->
      <div class="notice-section">
        <div class="section-title">
          <span>省市访问量</span>
          <el-link type="primary" @click="goToVisitLogs">查看全部</el-link>
        </div>
        <div class="page-visit-list">
          <div class="page-visit-item" v-for="(item, index) in provinceStats" :key="index">
            <div class="page-rank" :class="{'top3': index < 3}">{{ index + 1 }}</div>
            <div class="page-info">
              <div class="page-title">{{ item.name }}</div>
              <div class="page-url">{{ item.country === '中国' ? '中国' : item.country }}</div>
            </div>
            <div class="page-count">
              <span class="count-num">{{ item.count }}</span>
              <span class="count-label">次访问</span>
            </div>
          </div>
          <div class="empty-page-visit" v-if="provinceStats.length === 0">
            <i class="el-icon-map-location"></i>
            <span>暂无地区数据</span>
          </div>
        </div>
      </div>

      <!-- 渠道访问量统计 -->
      <div class="notice-section">
        <div class="section-title">
          <span>访问渠道</span>
          <el-link type="primary" @click="goToVisitLogs">查看全部</el-link>
        </div>
        <div class="channel-stats">
          <div class="channel-item" v-for="(item, index) in channelStats" :key="index">
            <div class="channel-icon" :class="item.key">
              <i :class="getChannelIcon(item.key)"></i>
            </div>
            <div class="channel-info">
              <div class="channel-name">{{ item.name }}</div>
              <div class="channel-url">{{ item.url }}</div>
            </div>
            <div class="channel-count">
              <span class="count-num">{{ item.count }}</span>
              <span class="count-label">次 ({{ item.percent }}%)</span>
            </div>
          </div>
          <div class="empty-page-visit" v-if="channelStats.length === 0">
            <i class="el-icon-share"></i>
            <span>暂无渠道数据</span>
          </div>
        </div>
      </div>

      <!-- 访问统计 -->
      <div class="chart-section">
        <div class="section-title">
          <span>访问趋势</span>
          <div class="chart-controls">
            <!-- 筛选按钮 -->
            <el-button type="text" size="mini" @click="showFilterDialog">
              <i class="el-icon-filter"></i> 筛选
            </el-button>
            <!-- 时间周期切换 -->
            <el-radio-group v-model="chartPeriod" size="mini" @change="handlePeriodChange">
              <el-radio-button label="day">今日</el-radio-button>
              <el-radio-button label="week">本周</el-radio-button>
              <el-radio-button label="month">本月</el-radio-button>
              <el-radio-button label="year">全年</el-radio-button>
            </el-radio-group>
          </div>
        </div>
        
        <!-- 已选筛选条件标签 -->
        <div class="filter-tags" v-if="hasActiveFilters">
          <el-tag v-if="filters.language" size="small" closable @close="clearFilter('language')">
            语言: {{ filters.language }}
          </el-tag>
          <el-tag v-if="filters.device" size="small" closable @close="clearFilter('device')">
            设备: {{ filters.device }}
          </el-tag>
          <el-tag v-if="filters.country" size="small" closable @close="clearFilter('country')">
            国家: {{ filters.country }}
          </el-tag>
          <el-tag v-if="filters.page_url" size="small" closable @close="clearFilter('page_url')">
            页面: {{ filters.page_url }}
          </el-tag>
          <el-button type="text" size="mini" @click="clearAllFilters">清除全部</el-button>
        </div>
        
        <div class="chart-container">
          <div class="chart-placeholder">
            <div ref="visitChart" class="echarts-container" v-if="chartData.length > 0"></div>
            <div class="chart-empty" v-else>
              <i class="el-icon-data-line"></i>
              <span>暂无数据</span>
            </div>
          </div>
          <div class="chart-summary">
            <div class="summary-item">
              <div class="summary-value">{{ trendStats.total_visits || 0 }}</div>
              <div class="summary-label">总访问量</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">{{ trendStats.unique_ips || 0 }}</div>
              <div class="summary-label">独立IP</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">{{ trendStats.avg_per_period || 0 }}</div>
              <div class="summary-label">平均/周期</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 筛选对话框 -->
    <el-dialog title="访问趋势筛选" :visible.sync="filterDialogVisible" width="500px">
      <el-form :model="filters" label-width="80px">
        <el-form-item label="时间范围">
          <el-radio-group v-model="filters.date_type" size="small">
            <el-radio-button label="day">今日</el-radio-button>
            <el-radio-button label="week">本周</el-radio-button>
            <el-radio-button label="month">本月</el-radio-button>
            <el-radio-button label="year">全年</el-radio-button>
            <el-radio-button label="custom">自定义</el-radio-button>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="自定义日期" v-if="filters.date_type === 'custom'">
          <el-date-picker
            v-model="filters.date_range"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            size="small"
          ></el-date-picker>
        </el-form-item>
        
        <el-form-item label="语言">
          <el-select v-model="filters.language" placeholder="全部语言" clearable size="small" style="width: 100%">
            <el-option 
              v-for="lang in filterOptions.languages" 
              :key="lang" 
              :label="lang" 
              :value="lang"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="设备类型">
          <el-select v-model="filters.device" placeholder="全部设备" clearable size="small" style="width: 100%">
            <el-option 
              v-for="device in filterOptions.devices" 
              :key="device" 
              :label="device" 
              :value="device"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="国家/地区">
          <el-select v-model="filters.country" placeholder="全部国家" clearable filterable size="small" style="width: 100%">
            <el-option 
              v-for="country in filterOptions.countries" 
              :key="country" 
              :label="country" 
              :value="country"
            ></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="页面路径">
          <el-select v-model="filters.page_url" placeholder="全部页面" clearable filterable size="small" style="width: 100%">
            <el-option 
              v-for="page in filterOptions.pages" 
              :key="page.page_url" 
              :label="page.page_title || page.page_url" 
              :value="page.page_url"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="filterDialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="applyFilters" size="small">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getVisitTrend, getDashboardFilterOptions, getVisitSources, getDashboardStats, getProvinceStats, getChannelStats } from '@/api/dashboard'

export default {
  name: 'HomePage',
  data() {
    return {
      userName: '管理员',
      currentDate: '',
      currentWeekday: '',
      chartPeriod: 'week',
      // 统计数据
      statsList: [
        { label: '今日访问', value: '0', icon: 'el-icon-view', bgColor: '#409EFF', trend: 0, route: '/website/visit' },
        { label: '产品数量', value: '0', icon: 'el-icon-goods', bgColor: '#67C23A', trend: 0, route: '/website/products' },
        { label: '新闻动态', value: '0', icon: 'el-icon-document', bgColor: '#E6A23C', trend: 0, route: '/website/news' },
        { label: '待处理留言', value: '0', icon: 'el-icon-message', bgColor: '#F56C6C', trend: 0, route: '/website/contact' }
      ],
      // 快捷入口
      quickAccessList: [
        { name: '产品管理', icon: 'el-icon-goods', color: '#409EFF', route: '/website/products' },
        { name: '新闻发布', icon: 'el-icon-document-add', color: '#67C23A', route: '/website/news' },
        { name: 'Banner设置', icon: 'el-icon-picture', color: '#E6A23C', route: '/website/banners' },
        { name: '解决方案', icon: 'el-icon-solution', color: '#F56C6C', route: '/website/solutions' },
        { name: '下载中心', icon: 'el-icon-download', color: '#909399', route: '/website/downloads' },
        { name: 'SEO设置', icon: 'el-icon-search', color: '#409EFF', route: '/website/seo-settings' },
        { name: '访问记录', icon: 'el-icon-data-line', color: '#67C23A', route: '/website/visit' },
        { name: '系统设置', icon: 'el-icon-setting', color: '#E6A23C', route: '/system/group' }
      ],
      // 待办事项
      todoList: [],
      // 各界面访问量
      pageVisitList: [],
      // 省市访问量统计
      provinceStats: [],
      // 渠道访问量统计
      channelStats: [],
      // 图表数据
      weekChartData: [],
      monthChartData: [],
      chartData: [],
      // 访问趋势统计
      trendStats: {},
      // 筛选相关
      filterDialogVisible: false,
      filters: {
        date_type: 'week',
        date_range: null,
        language: '',
        device: '',
        country: '',
        page_url: ''
      },
      filterOptions: {
        languages: [],
        devices: [],
        countries: [],
        pages: []
      },
      // ECharts 实例
      chartInstance: null
    }
  },
  computed: {
    hasActiveFilters() {
      return this.filters.language || this.filters.device || this.filters.country || this.filters.page_url
    },
    welcomeText() {
      const hour = new Date().getHours()
      if (hour < 12) return '早上好'
      if (hour < 18) return '下午好'
      return '晚上好'
    }
  },
  watch: {
    chartPeriod: {
      handler(newVal) {
        this.filters.date_type = newVal
        this.loadVisitTrend()
      },
      immediate: false
    }
  },
  mounted() {
    this.initDate()
    this.loadUserInfo()
    this.loadDashboardStats()
    this.loadFilterOptions()
    this.loadVisitTrend()
    this.loadPageVisits()
    this.loadProvinceStats()
    this.loadChannelStats()
  },
  methods: {
    initDate() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      this.currentDate = `${year}-${month}-${day}`
      
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      this.currentWeekday = weekdays[now.getDay()]
    },
    loadUserInfo() {
      // 从本地存储或API获取用户信息
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      if (userInfo.name) {
        this.userName = userInfo.name
      }
    },
    // 加载首页统计数据
    async loadDashboardStats() {
      try {
        const res = await getDashboardStats()
        if (res.code === 200) {
          const data = res.data
          // 更新统计数据卡片
          if (data.stats) {
            this.statsList[0].value = (data.stats.today_visits || 0).toLocaleString()
            this.statsList[1].value = (data.stats.product_count || 0).toLocaleString()
            this.statsList[2].value = (data.stats.news_count || 0).toLocaleString()
            this.statsList[3].value = (data.stats.pending_messages || 0).toLocaleString()
          }
          // 转换旧格式数据为新格式并更新图表
          if (data.week_visits) {
            this.weekChartData = {
              labels: data.week_visits.map(item => item.date),
              data: data.week_visits.map(item => item.count)
            }
          }
          if (data.month_visits) {
            this.monthChartData = {
              labels: data.month_visits.map(item => item.date),
              data: data.month_visits.map(item => item.count)
            }
          }
          // 根据当前选中的周期更新图表
          this.updateChartData()
          // 更新待办事项（最新留言）
          if (data.latest_messages && data.latest_messages.length > 0) {
            this.todoList = data.latest_messages.map(item => {
              const time = this.formatTimeAgo(item.created_at)
              return {
                title: `${item.category_name || '留言'}: ${item.name}`,
                time: time,
                priority: item.status === 0 ? 'high' : 'medium',
                route: '/website/contact'
              }
            })
          }
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
      }
    },
    // 格式化时间为相对时间
    formatTimeAgo(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)
      
      if (minutes < 1) return '刚刚'
      if (minutes < 60) return `${minutes}分钟前`
      if (hours < 24) return `${hours}小时前`
      if (days < 30) return `${days}天前`
      return dateStr.split(' ')[0]
    },
    // 加载访问趋势数据（新API）
    async loadVisitTrend() {
      try {
        const params = {
          date_type: this.filters.date_type
        }
        // 添加筛选条件
        if (this.filters.language) params.language = this.filters.language
        if (this.filters.device) params.device = this.filters.device
        if (this.filters.country) params.country = this.filters.country
        if (this.filters.page_url) params.page_url = this.filters.page_url
        // 自定义日期范围
        if (this.filters.date_type === 'custom' && this.filters.date_range && this.filters.date_range.length === 2) {
          params.date_start = this.filters.date_range[0]
          params.date_end = this.filters.date_range[1]
        }
        
        const res = await getVisitTrend(params)
        if (res.code === 200) {
          const data = res.data
          this.trendStats = data.stats || {}
          // 转换图表数据
          if (data.data && data.data.length > 0) {
            this.chartData = data.data.map(item => ({
              label: item.label,
              count: parseInt(item.count) || 0
            }))
            this.$nextTick(() => {
              this.renderChart()
            })
          } else {
            this.chartData = []
          }
        }
      } catch (error) {
        console.error('加载访问趋势失败:', error)
      }
    },
    
    // 根据当前周期更新图表数据
    updateChartData() {
      if (this.chartPeriod === 'week') {
        this.convertToChartData(this.weekChartData)
      } else if (this.chartPeriod === 'month') {
        this.convertToChartData(this.monthChartData)
      } else {
        // 今日、全年使用 loadVisitTrend 加载的数据
        this.loadVisitTrend()
      }
    },
    
    // 将 {labels, data} 格式转换为图表需要的格式
    convertToChartData(chartDataObj) {
      if (!chartDataObj || !chartDataObj.labels || !chartDataObj.data) {
        this.chartData = []
        return
      }
      this.chartData = chartDataObj.labels.map((label, index) => ({
        label: label,
        count: chartDataObj.data[index] || 0
      }))
      this.$nextTick(() => {
        this.renderChart()
      })
    },
    
    // 渲染 ECharts 图表
    renderChart() {
      if (!this.$refs.visitChart) return
      
      if (this.chartInstance) {
        this.chartInstance.dispose()
      }
      
      this.chartInstance = this.$echarts.init(this.$refs.visitChart)
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: '{b}: {c} 次访问'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.map(item => item.label),
          axisLine: { lineStyle: { color: '#dcdfe6' } },
          axisLabel: { color: '#606266', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#ebeef5' } },
          axisLabel: { color: '#606266', fontSize: 11 }
        },
        series: [{
          type: 'bar',
          data: this.chartData.map(item => item.count),
          itemStyle: {
            color: '#409EFF',
            borderRadius: [4, 4, 0, 0]
          },
          barWidth: '60%'
        }]
      }
      
      this.chartInstance.setOption(option)
    },
    
    // 加载筛选选项
    async loadFilterOptions() {
      try {
        const res = await getDashboardFilterOptions()
        if (res.code === 200) {
          this.filterOptions = res.data || {
            languages: [],
            devices: [],
            countries: [],
            pages: []
          }
        }
      } catch (error) {
        console.error('加载筛选选项失败:', error)
      }
    },
    
    // 显示筛选对话框
    showFilterDialog() {
      this.filterDialogVisible = true
    },
    
    // 应用筛选
    applyFilters() {
      this.filterDialogVisible = false
      this.loadVisitTrend()
    },
    
    // 清除单个筛选条件
    clearFilter(key) {
      this.filters[key] = ''
      this.loadVisitTrend()
    },
    
    // 清除全部筛选条件
    clearAllFilters() {
      this.filters.language = ''
      this.filters.device = ''
      this.filters.country = ''
      this.filters.page_url = ''
      this.loadVisitTrend()
    },
    
    // 处理周期切换
    handlePeriodChange(period) {
      this.filters.date_type = period
      this.loadVisitTrend()
    },
    // 跳转方法
    goToRoute(route) {
      if (route) {
        this.$router.push(route)
      }
    },
    goToDetail(route) {
      if (route) {
        this.$router.push(route)
      }
    },
    goToSettings() {
      this.$router.push('/system/group')
    },
    goToAllModules() {
      // 展开所有菜单
      this.$message.info('功能开发中')
    },
    goToAllNotices() {
      this.$message.info('功能开发中')
    },
    goToVisitLogs() {
      this.$router.push('/website/visit')
    },
    // 加载各界面访问量
    async loadPageVisits() {
      try {
        const res = await getVisitSources({ date_type: 'week' })
        if (res.code === 200 && res.data) {
          this.pageVisitList = res.data.pages || []
        }
      } catch (error) {
        console.error('加载页面访问量失败:', error)
      }
    },
    // 加载省市访问量统计
    async loadProvinceStats() {
      try {
        const res = await getProvinceStats({ date_type: 'week', limit: 10 })
        if (res.code === 200 && res.data) {
          this.provinceStats = res.data.provinces || []
        }
      } catch (error) {
        console.error('加载省市访问量失败:', error)
      }
    },
    // 加载渠道访问量统计
    async loadChannelStats() {
      try {
        const res = await getChannelStats({ date_type: 'week' })
        if (res.code === 200 && res.data) {
          this.channelStats = res.data.channels || []
        }
      } catch (error) {
        console.error('加载渠道访问量失败:', error)
      }
    },
    // 获取渠道图标
    getChannelIcon(type) {
      const icons = {
        direct: 'el-icon-s-home',
        baidu: 'el-icon-search',
        google: 'el-icon-search',
        bing: 'el-icon-search',
        other: 'el-icon-link'
      }
      return icons[type] || 'el-icon-link'
    },
    // 操作处理
    handleQuickAdd() {
      this.$message.success('快速添加功能')
    },
    handleTodo(item) {
      if (item.route) {
        this.$router.push(item.route)
      }
    },
    showNoticeDetail(item) {
      this.$alert(item.title, '公告详情', {
        confirmButtonText: '知道了',
        callback: () => {}
      })
    }
  }
}
</script>

<style scoped lang="scss">
.home-page {
  padding: 20px;
  background-color: var(--bg);
  min-height: calc(100vh - 60px);
}

/* 欢迎区 */
.welcome-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-200) 100%);
  border-radius: var(--border-radius);
  padding: 24px 30px;
  margin-bottom: 20px;
  color: #fff;
}

.welcome-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.welcome-info h2 {
  margin: 0 0 6px 0;
  font-size: 22px;
  font-weight: 500;
}

.welcome-info p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

.welcome-right {
  display: flex;
  gap: 12px;
}

.welcome-right .el-button {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}

.welcome-right .el-button--primary {
  background-color: #fff;
  border-color: #fff;
  color: var(--primary);
}

/* 统计数据卡片 */
.stats-section {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background-color: var(--bg-200);
  border-radius: var(--border-radius);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: var(--shadow);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #fff;
  flex-shrink: 0;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: var(--text-200);
  margin-top: 4px;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
}

.stat-trend.up {
  color: var(--success);
}

.stat-trend.down {
  color: var(--danger);
}

/* 中间区域 */
.middle-section {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

/* 快捷入口 */
.quick-access {
  background-color: var(--bg-200);
  border-radius: var(--border-radius);
  padding: 20px;
  box-shadow: var(--shadow);
}

.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 500;
  color: var(--text);
}

.quick-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.quick-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 10px;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.3s ease;
}

.quick-item:hover {
  background-color: var(--bg);
  transform: translateY(-2px);
}

.quick-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: 10px;
}

.quick-name {
  font-size: 13px;
  color: var(--text);
}

/* 待办事项 */
.todo-section {
  background-color: var(--bg-200);
  border-radius: var(--border-radius);
  padding: 20px;
  box-shadow: var(--shadow);
}

.todo-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.todo-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: var(--bg);
}

.todo-item:hover {
  background-color: var(--light);
}

.todo-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.todo-dot.high {
  background-color: var(--danger);
}

.todo-dot.medium {
  background-color: var(--warning);
}

.todo-dot.low {
  background-color: var(--success);
}

.todo-content {
  flex: 1;
  min-width: 0;
}

.todo-title {
  font-size: 14px;
  color: var(--text);
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.todo-time {
  font-size: 12px;
  color: var(--text-200);
}

.todo-arrow {
  color: var(--text-200);
  font-size: 12px;
}

.empty-todo {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: var(--text-200);
}

.empty-todo i {
  font-size: 32px;
  margin-bottom: 8px;
  color: var(--success);
}

/* 底部区域 */
.bottom-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

/* 各界面访问量 */
.notice-section {
  background-color: var(--bg-200);
  border-radius: var(--border-radius);
  padding: 20px;
  box-shadow: var(--shadow);
}

.page-visit-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.page-visit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--border-radius);
  background-color: var(--bg);
  transition: all 0.3s ease;
}

.page-visit-item:hover {
  background-color: var(--light);
}

.page-rank {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-200);
  background-color: var(--bg-200);
  flex-shrink: 0;
}

.page-rank.top3 {
  color: #fff;
  background-color: #409EFF;
}

.page-info {
  flex: 1;
  min-width: 0;
}

.page-title {
  font-size: 14px;
  color: var(--text);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-url {
  font-size: 12px;
  color: var(--text-200);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-count {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.count-num {
  font-size: 16px;
  font-weight: 600;
  color: #409EFF;
}

.count-label {
  font-size: 12px;
  color: var(--text-200);
}

.empty-page-visit {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--text-200);
  gap: 8px;
}

.empty-page-visit i {
  font-size: 32px;
}

/* 渠道统计 */
.channel-stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.channel-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: var(--border-radius);
  background-color: var(--bg);
  transition: all 0.3s ease;
}

.channel-item:hover {
  background-color: var(--light);
}

.channel-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 18px;
  flex-shrink: 0;
}

.channel-icon.direct {
  background-color: #409EFF;
  color: #fff;
}

.channel-icon.baidu {
  background-color: #2932E1;
  color: #fff;
}

.channel-icon.google {
  background-color: #4285F4;
  color: #fff;
}

.channel-icon.bing {
  background-color: #00809D;
  color: #fff;
}

.channel-icon.sogou {
  background-color: #FF6A00;
  color: #fff;
}

.channel-icon._360 {
  background-color: #19B955;
  color: #fff;
}

.channel-icon.other {
  background-color: #909399;
  color: #fff;
}

.channel-info {
  flex: 1;
  min-width: 0;
}

.channel-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text);
  margin-bottom: 4px;
}

.channel-url {
  font-size: 12px;
  color: var(--text-200);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.channel-percent {
  font-size: 12px;
  color: var(--text-200);
}

.channel-count {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

/* 图表区域 */
.chart-section {
  background-color: var(--bg-200);
  border-radius: var(--border-radius);
  padding: 20px;
  box-shadow: var(--shadow);
}

.chart-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);
}

.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-200);
}

.chart-empty i {
  font-size: 32px;
  margin-bottom: 8px;
}

.chart-container {
  display: flex;
  gap: 20px;
  align-items: flex-end;
}

.chart-placeholder {
  flex: 1;
  height: 260px;
  display: flex;
  align-items: flex-end;
}

.echarts-container {
  width: 100%;
  height: 100%;
}

.chart-summary {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 20px;
  border-left: 1px solid var(--border);
}

.summary-item {
  text-align: center;
}

.summary-value {
  font-size: 20px;
  font-weight: 600;
  color: var(--text);
}

.summary-label {
  font-size: 12px;
  color: var(--text-200);
  margin-top: 4px;
}

/* 响应式 */
@media (max-width: 1200px) {
  .stats-section {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .middle-section {
    grid-template-columns: 1fr;
  }
  
  .bottom-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .home-page {
    padding: 12px;
  }
  
  .welcome-section {
    flex-direction: column;
    gap: 16px;
    text-align: center;
    padding: 20px;
  }
  
  .welcome-left {
    flex-direction: column;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
  }
  
  .quick-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .chart-container {
    flex-direction: column;
  }
  
  .chart-summary {
    flex-direction: row;
    justify-content: space-around;
    padding-left: 0;
    padding-top: 16px;
    border-left: none;
    border-top: 1px solid var(--border);
    width: 100%;
  }
}
</style>
